import React, { useState, useMemo } from "react";
import { PlusIcon, MinusIcon } from "@heroicons/react/24/solid";
import EtGroupedActionTypeBlock from "./EtGroupedActionTypeBlock";

// 02 Feb 2026
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";


const EtGroupedLevelBlock = ({
  levelName,
  levelKey,
  levelOrActions,
  iconComponent: Icon,
  showState,
  setShowState,
  nextLevelLabel, // 15 Sep 2025
  renderNextLevelBlock,

  // 🔁 15 Sep 2025 - Master prop list for action handling
  showGroup,
  setShowGroup,
  actionNotes,
  actionProgress,
  showDetails,
  handleToggleDetails,
  handleNoteChange,
  handleProgressChange,
  handleSave,
  setSelectedAction,
  setShowEditModal,
  setSelectedActionToDelete,
  setShowDeleteModal,
  userId,
  trailId,
  sessionId,
//   getBgColor,
//   getIconClr,
//   getDaysDifference,
// 22 Dec 2025
performanceIndex,   // ✅ NEW
normalizedCollective, // 06 Jan 2026
levelValues, // 02 Feb 2026
}) => {

  // First Created - 14 Sep 2025
  // Author - Atul Pandey
  // Purpose - Render actions grouped by hierarchy level (Vision, Mission, Goal, GoalPeriod)
  // Updated - 15 Sep 2025 - Refined for master prop list for actions
  // Updated - 16 Sep 2025 - Refined for action count
  // Updated - 22 Dec 2025 - Refined for performanceIndex={performanceIndex}   // ✅ PASS THROUGH
  // Updated - 03 Jan 2026 - commented the prior performance display - will be refactored with mission and goals data later
  // Updated - 06 Jan 2026 - normalized collective for collective charting - pass through
  // Updated - 02 Feb 2026 - display target and rolled up actual value for a level - goal period, goal, mission - levelValues prop
  // Updated - 05 Feb 2026 - refined to include selfActual at all levels

  
  // 0 - Validate incoming props
  // console.log("----inside EtGroupedLevelBlock -----")
//   console.log("levelName", levelName)
  // console.log("levelKey", levelKey)
  // console.log("levelOrActions", levelOrActions)
//   console.log("showState", showState)
//   console.log("nextLevelLabel", nextLevelLabel)

  // console.log("performanceIndex", performanceIndex)
  // console.log("levelValues", levelValues)

  // Expand/collapse control for this levelKey
  const isExpanded = showState[levelKey] || false;

  // 🧭 Tabs per level (Vision, Mission, Goal)
  const [levelTab, setLevelTab] = useState(
    levelName === "Vision"
      ? "visionActions"
      : levelName === "Mission"
      ? "missionActions"
      : levelName === "Goal"
      ? "goalActions"
      : "goalPeriodActions"
  );

  // 🎨 Background style based on level
  const levelBgMap = {
    Vision: "bg-white",
    Mission: "bg-orange-50",
    Goal: "bg-gray-300",
    "Goal Period": "bg-orange-100",
  };

  // Separate actionType blocks and next level entries
  const actionTypeEntries = Object.entries(levelOrActions).filter(
    ([_, val]) => Array.isArray(val)
  );

  // 16 Sep 2025 - action count
  const countNestedActions = (node) => {
    if (Array.isArray(node)) {
        return node.length;
    }

    if (typeof node === 'object' && node !== null) {
        return Object.values(node).reduce((acc, val) => acc + countNestedActions(val), 0);
    }

    return 0;
  };

    const totalActions = countNestedActions(levelOrActions);
    const actionsHere = actionTypeEntries.reduce((acc, [_, actions]) => acc + actions.length, 0);
    const nextLevelTotal = totalActions - actionsHere;

//   const totalActions = actionTypeEntries.reduce((acc, [_, actions]) => acc + actions.length, 0);

  const nextLevelEntries = Object.entries(levelOrActions).filter(
    ([_, val]) => typeof val === "object" && !Array.isArray(val)
  );

  // console.log('actionTypeEntries', actionTypeEntries)
//   console.log('nextLevelEntries', nextLevelEntries)

// 22 Dec 2025 - Performance Self Roll up
const selfOutcomeRollup = useMemo(() => {
  if (!performanceIndex?.Self?.Action) return null;

  let achieved = 0;
  let target = 0;

  actionTypeEntries.forEach(([_, actions]) => {
    actions.forEach(action => {
      const snapshot =
        performanceIndex.Self.Action.get(action._id);

      if (!snapshot || snapshot.role !== "Outcome") return;

      achieved += snapshot.achievedValue || 0;
      target += snapshot.targetValue || 0;
    });
  });

  if (target === 0) return null;

  return {
    achieved,
    target,
    progressPercent: Math.round((achieved / target) * 100),
  };
}, [actionTypeEntries, performanceIndex]);

// 22 Dec 2025 - Performance Roll up - Collective Outcome Actions
const collectiveOutcomeRollup = useMemo(() => {
  if (!performanceIndex?.Collective?.Action) return null;

  let achieved = 0;
  let target = 0;

  actionTypeEntries.forEach(([_, actions]) => {
    actions.forEach(action => {
      const snapshot =
        performanceIndex.Collective.Action.get(action._id);

      if (!snapshot || snapshot.role !== "Outcome") return;

      achieved += snapshot.achievedValue || 0;

      // ⚠️ Target comes from sponsor snapshot only once
      target = Math.max(target, snapshot.targetValue || 0);
    });
  });

  if (target === 0) return null;

  return {
    achieved,
    target,
    progressPercent: Math.round((achieved / target) * 100),
  };
}, [actionTypeEntries, performanceIndex]);


// ✅ DEBUG LOG 
// console.log(
//   "Self rollup",
//   levelName,
//   levelKey,
//   selfOutcomeRollup
// );

  return (
    <div key={levelName} className={`p-1 m-1 rounded ${levelBgMap[levelName]}`}>
      {/* Header with toggle */}
      <div
        className="grid grid-cols-11 justify-start items-center px-2 py-1 cursor-pointer"
        onClick={() =>
          setShowState((prev) => ({ ...prev, [levelKey]: !prev[levelKey] }))
        }
      >
        
            <div className="col-span-6 flex items-center m-1 p-1">
                {isExpanded ? (
                        <MinusIcon className="h-4 w-4 text-orange-600" />
                    ) : (
                        <PlusIcon className="h-4 w-4 text-orange-600" />
                    )}
                <div className="mx-1 px-1">
                    <Icon className="text-orange-600 h-5 w-5" />
                </div>
                <p className="text-sm mx-2 font-medium">
                    {levelName} :
                </p>
                <p className="font-semibold text-sm mx-2">{levelKey}</p>
            </div>
          
          <div className="col-span-1 flex items-center space-x-2">
            {/* <p className="font-semibold text-sm text-gray-700">{levelKey}</p> */}
            <div className="rounded-full flex justify-center items-center h-5 w-5 border border-orange-300 bg-gray-50">
                <p className="text-orange-600 text-[10px]">{totalActions}</p>
            </div>

          </div>

          {levelValues && (
            <div className="col-span-4 grid grid-cols-4 items-start gap-4 text-xs">

              {/* Achieved % */}
              <div className="w-10 h-10">
                <CircularProgressbar
                  value={
                    levelValues.target
                      ? Math.round((levelValues.actual / levelValues.target) * 100)
                      : 0
                  }
                  text={`${levelValues.target
                    ? Math.round((levelValues.actual / levelValues.target) * 100)
                    : 0} %`}
                  styles={buildStyles({
                    textSize: "28px",
                    pathColor: "#ea580c",
                    textColor: "#374151",
                    trailColor: "#e5e7eb",
                  })}
                />
              </div>

              {/* Numbers Grid */}
              <div className="col-span-3 grid grid-cols-3 gap-x-6">

                {/* Actuals */}
                {/* <div>
                <p className="text-gray-500">Actuals</p>
                <div className="mt-1 flex items-center gap-1">
                  <div className="flex items-center">
                    <span className="text-orange-600 mr-1">All:</span>
                    <span className="font-semibold">{levelValues.actual}</span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-orange-600 mr-1">Self:</span>
                    <span className="font-semibold">{levelValues.selfActual}</span>
                  </div>
                </div>
              </div> */}

                <div>
                  <p className="text-gray-500">Actuals</p>
                  <div className="mt-1 space-y-1">
                    <div className="flex items-center">
                      <span className="text-orange-600 mr-1">All:</span>
                      <span className="font-semibold">{levelValues.actual}</span>
                    </div>
                    <div className="flex items-center">
                      <span className="text-orange-600 mr-1">Self:</span>
                      <span className="font-semibold">{levelValues.selfActual}</span>
                    </div>
                  </div>
                </div>

                {/* Target */}
                <div className="ml-3">
                  <p className="text-gray-500">Target</p>
                  <p className="font-semibold mt-1">{levelValues.target}</p>
                </div>

                {/* UoM */}
                <div>
                  <p className="text-gray-500">UoM</p>
                  <p className="font-semibold mt-1">{levelValues.uom}</p>
                </div>

              </div>
            </div>
          )}


        
         {/* {levelValues && (
          <div className="col-span-4 flex items-center space-x-2">
    
              <div className="ml-3 text-xs text-gray-700">
                <p>Actual: {levelValues.actual}</p> 
                
              </div>
              <div className="ml-3 text-xs text-gray-700">
               
                <p>Target: {levelValues.target}</p> 
                
              </div>
              <div className="ml-3 text-xs text-gray-700">
                
                <p>UoM: {levelValues.uom}</p> 
              </div>
              <div className="ml-3 text-xs text-gray-700">
                
                <p>Achieved: {levelValues.actual/levelValues.target}</p> 
              </div>
            
          </div>
          )} */}
      </div>

      {/* 22 Dec 2025 - Performance Roll up - Self Outcome Actions */}
      {/* {selfOutcomeRollup && (
        <div className="bg-gray-50 border rounded px-3 py-2 mb-2">
          <div className="grid grid-cols-3 gap-4 text-xs">
            <div>
              <p className="font-semibold">Achieved</p>
              <p>{selfOutcomeRollup.achieved}</p>
            </div>

            <div>
              <p className="font-semibold">Target</p>
              <p>{selfOutcomeRollup.target}</p>
            </div>

            <div>
              <p className="font-semibold">Progress</p>
              <p>{selfOutcomeRollup.progressPercent}%</p>
            </div>
          </div>
        </div>
      )} */}

      {/* 22 Dec 2025 - Performance Roll up - Collective Outcome Actions */}
      {/* {collectiveOutcomeRollup && (
        <div className="bg-orange-50 border rounded px-3 py-2 mb-2">
          <div className="grid grid-cols-3 gap-4 text-xs">
            <div>
              <p className="font-semibold">Collective Achieved</p>
              <p>{collectiveOutcomeRollup.achieved}</p>
            </div>

            <div>
              <p className="font-semibold">Collective Target</p>
              <p>{collectiveOutcomeRollup.target}</p>
            </div>

            <div>
              <p className="font-semibold">Progress</p>
              <p>{collectiveOutcomeRollup.progressPercent}%</p>
            </div>
          </div>
        </div>
      )} */}



      {isExpanded && (
        <div className="m-1 p-1 bg-white rounded">
          {/* 🧭 Tabs per level */}
          <div className="flex space-x-4 border-b border-orange-300 mb-2 ml-2">
            {/* Always render actions tab */}
            <button
            onClick={() => setLevelTab(
                levelName === "Vision"
                ? "visionActions"
                : levelName === "Mission"
                ? "missionActions"
                : levelName === "Goal"
                ? "goalActions"
                : "goalPeriodActions"
            )}
            className={`flex items-center space-x-2 px-3 py-1 text-sm font-semibold border-b-2 ${
                levelTab.includes("Actions")
                ? "border-orange-600 text-orange-600"
                : "border-transparent text-gray-400"
            }`}
            >
                <p>{levelName} Actions</p>
                <div className="rounded-full flex justify-center items-center h-5 w-5 border border-orange-300 bg-gray-50">
                    <p className="text-orange-600 text-[10px]">{actionsHere}</p>
                </div>
            </button>

            {/* <button
              onClick={() =>
                setLevelTab(
                  levelName === "Vision"
                    ? "visionActions"
                    : levelName === "Mission"
                    ? "missionActions"
                    : levelName === "Goal"
                    ? "goalActions"
                    : "goalPeriodActions"
                )
              }
              className={`px-3 py-1 text-sm font-semibold border-b-2 ${
                levelTab.includes("Actions")
                  ? "border-orange-600 text-orange-600"
                  : "border-transparent text-gray-400"
              }`}
            >
              {levelName} Actions
            </button> */}

            {/* Conditionally render next level tab */}
            {nextLevelEntries.length > 0 && nextLevelLabel && (
            <button
                onClick={() =>
                setLevelTab(
                    levelName === "Vision"
                    ? "missions"
                    : levelName === "Mission"
                    ? "goals"
                    : levelName === "Goal"
                    ? "goalPeriods" : ""
                )
                }
                className={`flex items-center space-x-2 px-3 py-1 text-sm font-semibold border-b-2 ${
                !levelTab.includes("Actions")
                    ? "border-orange-600 text-orange-600"
                    : "border-transparent text-gray-400"
                }`}
            >
                <p>{nextLevelLabel}s</p>
                <div className="rounded-full flex justify-center items-center h-5 w-5 border border-orange-300 bg-gray-50">
                <p className="text-orange-600 text-[10px]">{nextLevelTotal}</p>
                </div>
            </button>
            )}

            {/* {nextLevelEntries.length > 0 && nextLevelLabel && (
              <button
                onClick={() =>
                  setLevelTab(
                    levelName === "Vision"
                      ? "missions"
                      : levelName === "Mission"
                      ? "goals"
                      : levelName === "Goal"
                      ? "goalPeriods" : ""
                  )
                }
                className={`px-3 py-1 text-sm font-semibold border-b-2 ${
                  !levelTab.includes("Actions")
                    ? "border-orange-600 text-orange-600"
                    : "border-transparent text-gray-400"
                }`}
              >
                {nextLevelLabel}s
              </button>
            )} */}
          </div>

          {/* Action Cards Block */}
          {levelTab.includes("Actions") && (
            <EtGroupedActionTypeBlock 
            actionTypeEntries={actionTypeEntries} 
            performanceIndex={performanceIndex}   // ✅ 22 Dec 2025 PASS THROUGH
            normalizedCollective={normalizedCollective} // 06 Jan 2026 - PASS THROUGH
            // resolveSharedGoalPeriodForAction={resolveSharedGoalPeriodForAction} // 02 Feb 2026 - PASS THROUGH
            
            // 15 Sep 2025 - Full prop list for Actions
            showGroup={showGroup}
            setShowGroup={setShowGroup}
            actionNotes={actionNotes}
            actionProgress={actionProgress}
            showDetails={showDetails}
            handleToggleDetails={handleToggleDetails}
            handleNoteChange={handleNoteChange}
            handleProgressChange={handleProgressChange}
            handleSave={handleSave}
            setSelectedAction={setSelectedAction}
            setShowEditModal={setShowEditModal}
            setSelectedActionToDelete={setSelectedActionToDelete}
            setShowDeleteModal={setShowDeleteModal}
            userId={userId}
            trailId={trailId}
            sessionId={sessionId}
            // getBgColor={getBgColor}
            // getIconClr={getIconClr}
            // getDaysDifference={getDaysDifference}
            
            />
          )}

          {/* Next Level Block */}
          {levelTab !== `${levelName.toLowerCase()}Actions` &&
            nextLevelEntries.map(([nextKey, nextOrActions]) => (
              <div key={nextKey}>
                {renderNextLevelBlock?.({
                  levelKey: nextKey,
                  levelOrActions: nextOrActions,
                })}
              </div>
            ))}
        </div>
      )}
    </div>
  );
};

export default EtGroupedLevelBlock;